import { requireSupabase } from './supabase'
import type {
  AuditEventRow,
  AttachmentMetadata,
  CandidateChange,
  ExportRow,
  GeneralReference,
  GeneralReferenceRow,
  GeneralReferenceVersionRow,
  ImportRow,
  NotificationPreferences,
  OperatorProfile,
  ProjectAttachment,
  ProjectRow,
  ProjectSnapshot,
  ProjectVersionRow,
  PresentationDraftConfig,
  PresentationEditionRow,
  PresentationSourceSnapshot,
} from '../types'

export async function listProjects(): Promise<ProjectRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_projects')
    .select('*')
    .is('deleted_at', null)
    .order('name')
  if (error) throw error
  return (data ?? []) as ProjectRow[]
}

export async function listTrashedProjects(): Promise<ProjectRow[]> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_list_trashed_projects')
  if (error) throw error
  return (data ?? []) as ProjectRow[]
}

export async function trashProject(project: ProjectRow): Promise<ProjectRow> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_trash_project', {
    p_project_id: project.id,
    p_expected_version: project.version,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as ProjectRow
}

export async function restoreTrashedProject(projectId: string): Promise<ProjectRow> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_restore_trashed_project', {
    p_project_id: projectId,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as ProjectRow
}

export async function getMyOperatorProfile(): Promise<OperatorProfile> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_operators')
    .select('id, email, display_name, role, active, business_data_access, phone, notification_preferences, updated_at')
    .single()
  if (error) throw error
  return data as OperatorProfile
}

export async function updateMyOperatorProfile(input: {
  displayName: string
  phone?: string
  notificationPreferences: NotificationPreferences
}): Promise<OperatorProfile> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_update_my_profile', {
    p_display_name: input.displayName,
    p_phone: input.phone ?? null,
    p_notification_preferences: input.notificationPreferences,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as OperatorProfile
}

export async function getGeneralReference(): Promise<GeneralReferenceRow> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_references')
    .select('*')
    .eq('reference_key', 'building-permit-ci')
    .single()
  if (error) throw error
  return data as GeneralReferenceRow
}

export async function listGeneralReferenceVersions(referenceKey: string): Promise<GeneralReferenceVersionRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_reference_versions')
    .select('*')
    .eq('reference_key', referenceKey)
    .order('version', { ascending: false })
  if (error) throw error
  return (data ?? []) as GeneralReferenceVersionRow[]
}

export async function saveGeneralReference(
  reference: GeneralReferenceRow,
  content: GeneralReference,
  summary: string,
): Promise<GeneralReferenceRow> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_update_reference', {
    p_reference_key: reference.reference_key,
    p_expected_version: reference.version,
    p_reference: content,
    p_change_summary: summary,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as GeneralReferenceRow
}

export async function getProject(projectId: string): Promise<ProjectRow> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_projects')
    .select('*')
    .eq('id', projectId)
    .single()
  if (error) throw error
  return data as ProjectRow
}

export async function saveProjectSnapshot(
  project: ProjectRow,
  snapshot: ProjectSnapshot,
  summary: string,
): Promise<ProjectRow> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_update_project_snapshot', {
    p_project_id: project.id,
    p_expected_version: project.version,
    p_snapshot: snapshot,
    p_change_summary: summary,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as ProjectRow
}

export async function createProject(
  input: { slug: string; code: string; name: string; scenario: string; city: string },
  snapshot: ProjectSnapshot,
): Promise<ProjectRow> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_create_project', {
    p_slug: input.slug,
    p_code: input.code,
    p_name: input.name,
    p_scenario: input.scenario,
    p_city: input.city,
    p_snapshot: snapshot,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return row as ProjectRow
}

export async function listProjectVersions(projectId: string): Promise<ProjectVersionRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_project_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('version', { ascending: false })
  if (error) throw error
  return (data ?? []) as ProjectVersionRow[]
}

export async function listProjectAttachments(projectId: string): Promise<ProjectAttachment[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_attachments')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as ProjectAttachment[]
}

export async function listProjectImports(projectId: string): Promise<ImportRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_imports')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as ImportRow[]
}

export async function listProjectExports(projectId: string): Promise<ExportRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_exports')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as ExportRow[]
}

export async function listProjectAuditEvents(projectId: string): Promise<AuditEventRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_audit_events')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as AuditEventRow[]
}

type UploadAttachmentInput = {
  project: ProjectRow
  stepId?: string
  file: File
  metadata?: AttachmentMetadata
  replaces?: ProjectAttachment
  stepLabel?: string
}

export async function uploadProjectAttachment({
  project,
  stepId,
  file,
  metadata = {},
  replaces,
  stepLabel,
}: UploadAttachmentInput): Promise<{ attachment: ProjectAttachment; project: ProjectRow }> {
  const client = requireSupabase()
  const { data: userData } = await client.auth.getUser()
  if (!userData.user) throw new Error('Session expirée.')
  if (file.size > 26_214_400) throw new Error('Ce fichier dépasse la limite de 25 Mo.')

  const attachmentId = crypto.randomUUID()
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'piece-jointe'
  const storagePath = `${userData.user.id}/${project.id}/${attachmentId}-${safeName}`
  const { error: uploadError } = await client.storage
    .from('suiviaziz-attachments')
    .upload(storagePath, file, { upsert: false, contentType: file.type || 'application/octet-stream' })
  if (uploadError) throw uploadError

  const action = replaces ? `Nouvelle version de « ${replaces.title || replaces.filename} »` : `Pièce jointe « ${file.name} » ajoutée`
  const summary = stepLabel ? `${action} · ${stepLabel}` : action
  const snapshot = structuredClone(project.snapshot)
  snapshot.project.events.unshift({
    id: crypto.randomUUID(),
    date: snapshot.as_of_date,
    type: replaces ? 'nouvelle_version_piece_jointe' : 'piece_jointe',
    step_id: stepId,
    summary,
  })

  const { data, error } = await client.rpc('suiviaziz_register_attachment', {
    p_attachment_id: attachmentId,
    p_project_id: project.id,
    p_expected_version: project.version,
    p_step_id: stepId ?? null,
    p_filename: file.name,
    p_storage_path: storagePath,
    p_mime_type: file.type || null,
    p_file_size: file.size,
    p_title: metadata.title || replaces?.title || null,
    p_category: metadata.category || replaces?.category || null,
    p_document_date: metadata.document_date || replaces?.document_date || null,
    p_received_date: metadata.received_date || replaces?.received_date || null,
    p_issuer: metadata.issuer || replaces?.issuer || null,
    p_note: metadata.note || replaces?.note || null,
    p_replaces_attachment_id: replaces?.id ?? null,
    p_snapshot: snapshot,
    p_change_summary: summary,
  })

  if (error) {
    await client.storage.from('suiviaziz-attachments').remove([storagePath])
    throw error
  }

  const payload = data as { attachment: ProjectAttachment; project: ProjectRow }
  return payload
}

export async function updateAttachmentMetadata(
  attachmentId: string,
  metadata: AttachmentMetadata,
): Promise<ProjectAttachment> {
  const { data, error } = await requireSupabase().rpc('suiviaziz_update_attachment_metadata', {
    p_attachment_id: attachmentId,
    p_title: metadata.title || null,
    p_category: metadata.category || null,
    p_document_date: metadata.document_date || null,
    p_received_date: metadata.received_date || null,
    p_issuer: metadata.issuer || null,
    p_note: metadata.note || null,
  })
  if (error) throw error
  return data as ProjectAttachment
}

export async function downloadProjectAttachment(attachment: ProjectAttachment) {
  const data = await downloadStorageBlob('suiviaziz-attachments', attachment.storage_path)
  const url = URL.createObjectURL(data)
  const link = document.createElement('a')
  link.href = url
  link.download = attachment.filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

export async function downloadStorageBlob(bucket: string, path: string): Promise<Blob> {
  const { data, error } = await requireSupabase().storage
    .from(bucket)
    .download(path)
  if (error) throw error
  return data
}

export async function registerExport(project: ProjectRow, format: ExportRow['format'] = 'xlsx'): Promise<ExportRow> {
  const formatMarker = format === 'docx' ? '-DOCX' : ''
  const exportId = `EXP-${project.code}${formatMarker}-${new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 17)}`
  const { data, error } = await requireSupabase()
    .from('suiviaziz_exports')
    .insert({
      export_id: exportId,
      project_id: project.id,
      project_version: project.version,
      format,
      source_snapshot: project.snapshot,
    })
    .select('*')
    .single()
  if (error) throw error
  return data as ExportRow
}

export async function getExport(exportId: string): Promise<ExportRow> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_exports')
    .select('*')
    .eq('export_id', exportId)
    .single()
  if (error) throw error
  return data as ExportRow
}

export async function storeImport(
  project: ProjectRow,
  exportRow: ExportRow,
  file: File,
  candidates: CandidateChange[],
): Promise<ImportRow> {
  const client = requireSupabase()
  const { data: userData } = await client.auth.getUser()
  if (!userData.user) throw new Error('Session expirée.')

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const importId = `IMP-${project.code}-${new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)}`
  const storagePath = `${userData.user.id}/${project.id}/${importId}-${safeName}`
  const contentType = exportRow.format === 'docx'
    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const { error: uploadError } = await client.storage
    .from('suiviaziz-imports')
    .upload(storagePath, file, { upsert: false, contentType: file.type || contentType })
  if (uploadError) throw uploadError

  const { data, error } = await client
    .from('suiviaziz_imports')
    .insert({
      import_id: importId,
      project_id: project.id,
      export_id: exportRow.export_id,
      source_project_version: exportRow.project_version,
      filename: file.name,
      storage_path: storagePath,
      format: exportRow.format,
      status: 'à vérifier',
      candidates,
    })
    .select('*')
    .single()
  if (error) throw error
  return data as ImportRow
}

export async function markImportApplied(importRow: ImportRow, candidates: CandidateChange[]) {
  const { error } = await requireSupabase()
    .from('suiviaziz_imports')
    .update({ status: 'appliqué', candidates, applied_at: new Date().toISOString() })
    .eq('id', importRow.id)
  if (error) throw error
}

export async function listPresentationEditions(projectId: string): Promise<PresentationEditionRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_presentation_editions')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as PresentationEditionRow[]
}

export async function listGeneralPresentationEditions(referenceKey: string): Promise<PresentationEditionRow[]> {
  const { data, error } = await requireSupabase()
    .from('suiviaziz_presentation_editions')
    .select('*')
    .eq('scope', 'general')
    .eq('reference_key', referenceKey)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as PresentationEditionRow[]
}

export async function publishPresentationEdition(input: {
  id: string
  editionId: string
  project: ProjectRow
  reference?: GeneralReferenceRow
  config: PresentationDraftConfig
  source: PresentationSourceSnapshot
  pptx: Blob
  pptxSha256: string
}): Promise<PresentationEditionRow> {
  const client = requireSupabase()
  const { data: userData } = await client.auth.getUser()
  if (!userData.user) throw new Error('Session expirée.')
  const basePath = `${userData.user.id}/${input.id}`
  const pptxStoragePath = `${basePath}/presentation.pptx`
  const uploaded: string[] = []

  try {
    const { error: pptxError } = await client.storage
      .from('suiviaziz-presentations')
      .upload(pptxStoragePath, input.pptx, { upsert: false, contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' })
    if (pptxError) throw pptxError
    uploaded.push(pptxStoragePath)

    const title = input.config.scope === 'general' ? 'Parcours administratif général' : input.project.name
    const general = input.config.scope === 'general'
    if (general && !input.reference) throw new Error('La version de référence est absente.')
    const { data, error } = await client.rpc('suiviaziz_register_contextual_presentation_edition', {
      p_id: input.id,
      p_edition_id: input.editionId,
      p_project_id: general ? null : input.project.id,
      p_expected_project_version: general ? null : input.project.version,
      p_reference_key: general ? input.reference!.reference_key : null,
      p_expected_reference_version: general ? input.reference!.version : null,
      p_source_reference_id: input.project.snapshot.reference.id,
      p_scope: input.config.scope,
      p_as_of_date: general ? input.reference!.content.effective_date : input.project.snapshot.as_of_date,
      p_title: title,
      p_audience: input.config.audience || null,
      p_objective: input.config.objective || null,
      p_duration: input.config.duration,
      p_presentation_config: input.config,
      p_source_snapshot: input.source,
      p_pptx_storage_path: pptxStoragePath,
      p_pdf_storage_path: null,
      p_pptx_sha256: input.pptxSha256,
      p_pdf_sha256: null,
    })
    if (error) throw error
    return data as PresentationEditionRow
  } catch (error) {
    if (uploaded.length) await client.storage.from('suiviaziz-presentations').remove(uploaded)
    throw error
  }
}

export async function downloadPresentationEdition(edition: PresentationEditionRow) {
  const storagePath = edition.pptx_storage_path
  const { data, error } = await requireSupabase().storage
    .from('suiviaziz-presentations')
    .download(storagePath)
  if (error) throw error
  const safeTitle = edition.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '')
  const url = URL.createObjectURL(data)
  const link = document.createElement('a')
  link.href = url
  link.download = `${safeTitle}_${edition.edition_id}.pptx`
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
